import { useCallback, useContext, useEffect, useState } from 'react';
import { IPaginationParams, IStation } from '@helpers/types.ts';
import {
  InductionRoute,
  Simulation,
} from '@helpers/types.ts';
import {
  IStationSwitcherContext,
  StationSwitcherContext,
} from '@components/providers/station/context.tsx';
import { GridRowSelectionModel } from '@mui/x-data-grid';
import { useDownload } from '@hooks/use-download.ts';
import { chain } from 'lodash';
import { endOfDay, startOfDay } from 'date-fns';
import { useHttp } from '@hooks/use-http';

export const useSimulationsPage = (filters: IPaginationParams) => {
  const http = useHttp();
  const { downloadXlsx } = useDownload();
  const { destStation: station } = useContext<IStationSwitcherContext>(
    StationSwitcherContext,
  );
  const [_error, setError] = useState<string>('');
  const [date, setDate] = useState<Date>(new Date());
  const [total, setTotal] = useState<number>(0);
  const [rows, setRows] = useState<Array<Simulation>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [inductSelection, setInductSelection] = useState<Array<string>>([]);
  const [loadingDownload, setLoadingDownload] = useState<boolean>(false);
  const [loadingInduction, setLoadingInduction] = useState<boolean>(false);
  const [routes, setRoutesInduction] = useState<Array<InductionRoute>>([]);
  const [show_induction, setShowInduction] = useState<boolean>(false);

  const onChangeDate = useCallback((date: Date) => {
    const now = new Date();
    date.setHours(now.getHours(), now.getMinutes(), now.getSeconds());
    setDate(date);
  }, []);
  const startInduction = useCallback(async () => {
    try {
      setLoadingInduction(true);
      const routes = await http.lastmile.getRoutes(inductSelection);
      setRoutesInduction(routes);
      setShowInduction(true);
    } catch (e: any) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoadingInduction(false);
    }
  }, [inductSelection, http]);
  const closeInduction = useCallback(() => {
    setShowInduction(false);
  }, []);

  const onDownload = useCallback(async () => {
    try {
      setLoadingDownload(true);
      const routes = await http.lastmile.getRoutes(inductSelection);

      const excel: any[] = [];
      routes.forEach((route: any) => {
        excel.push(
          ...chain(route.orders)
            .sortBy('delivery_order')
            .map((order) => {
              return {
                'date[utc]': route['date[utc]'],
                'week[utc]': route['week[utc]'],
                station: route.station,
                plan: route.name,
                tracking_id: order.tracking_id,
                internal_key: order.internal_key,
                stop: order.delivery_order,
                lat: order.geometry.coordinates[1],
                lon: order.geometry.coordinates[0],
                inducido: order.inducted,
                desactivado: order.disabled,
              };
            })
            .value(),
        );
      });

      downloadXlsx(
        excel,
        `induction-${new Date().toISOString().replace(/:/g, '-')}`,
      );
    } catch (e: any) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoadingDownload(false);
    }
  }, [inductSelection, http, downloadXlsx]);

  const fetchSimulations = useCallback(
    async (filters: IPaginationParams = {}, station: IStation, date: Date) => {
      if (!station) {
        return;
      }
      try {
        setLoading(true);
        const result = await http.lastmile.getPlans({
          page: filters.page,
          sort: filters.sort,
          page_size: filters.page_size,
          search: {
            ...filters.search,
            station: station.station,
            from_date: startOfDay(date),
            to_date: endOfDay(date),
          },
        });
        setRows(result.data);
        setTotal(result.total);
      } catch (e: any) {
        setError(e.response?.data?.message || e.message);
      } finally {
        setLoading(false);
      }
    },
    [http],
  );

  const onRowSelectionModelChange = useCallback(
    (selection: GridRowSelectionModel) => {
      setInductSelection(selection as unknown as Array<string>);
    },
    [],
  );

  useEffect(() => {
    if (station) {
      fetchSimulations(filters, station, date).then(() => {});
    }
  }, [filters, station, date, fetchSimulations]);

  return {
    rows,
    date,
    total,
    loading,
    routes,
    onDownload,
    onChangeDate,
    show_induction,
    startInduction,
    closeInduction,
    inductSelection,
    loadingDownload,
    loadingInduction,
    onRowSelectionModelChange,
  };
};